/**
 * 安全区探测与 CSS 变量注入
 *
 * 与 public/index.html 中 viewport-fit=cover 配套；部分手机/平板在「添加到主屏幕」
 * 或桌面快捷方式（display-mode: standalone）下 env(safe-area-inset-*) 可能恒为 0，
 * 需 JS 探测 + 保守 fallback。
 */
import { isPwaStandalone } from './pwaInstall'

let isInitialized = false

function debounce (fn, wait) {
  let timer
  return (...args) => {
    clearTimeout(timer)
    timer = setTimeout(() => fn(...args), wait)
  }
}

/** 通过探针 div 读取 env(safe-area-inset-*) 计算值（px） */
function measureEnvSafeAreaInsets () {
  if (typeof document === 'undefined') {
    return { top: 0, right: 0, bottom: 0, left: 0 }
  }

  const probe = document.createElement('div')
  probe.setAttribute('aria-hidden', 'true')
  probe.style.cssText = [
    'position:fixed',
    'top:0',
    'left:0',
    'visibility:hidden',
    'pointer-events:none',
    'z-index:-1',
    'padding:constant(safe-area-inset-top) constant(safe-area-inset-right) constant(safe-area-inset-bottom) constant(safe-area-inset-left)',
    'padding:env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom) env(safe-area-inset-left)'
  ].join(';')

  document.documentElement.appendChild(probe)
  const cs = getComputedStyle(probe)
  const insets = {
    top: parseFloat(cs.paddingTop) || 0,
    right: parseFloat(cs.paddingRight) || 0,
    bottom: parseFloat(cs.paddingBottom) || 0,
    left: parseFloat(cs.paddingLeft) || 0
  }
  document.documentElement.removeChild(probe)
  return insets
}

function isAppleTouchDevice () {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent || ''
  if (/iPhone|iPad|iPod/i.test(ua)) return true
  return navigator.platform === 'MacIntel' && (navigator.maxTouchPoints || 0) > 1
}

/**
 * env() 为 0 时，独立窗口 / 桌面快捷方式的保守顶部 fallback（px）
 * 仅用于 standalone，避免普通浏览器 tab 误加顶距
 */
function getStandaloneTopFallback () {
  if (!isPwaStandalone()) return 0

  if (isAppleTouchDevice()) {
    const maxDim = Math.max(window.screen.width, window.screen.height)
    // iPhone X 及以上竖屏典型安全区约 47px；旧款约 20px
    return maxDim >= 812 ? 47 : 20
  }

  // Android / 其它 WebView 独立窗口状态栏高度
  return 24
}

/** 写入 :root CSS 变量并标记 html[data-safe-area-ready] */
export function updateSafeAreaVars () {
  if (typeof document === 'undefined') return

  const measured = measureEnvSafeAreaInsets()
  const standaloneTopFallback = measured.top <= 0 ? getStandaloneTopFallback() : 0
  const top = Math.max(measured.top, standaloneTopFallback)

  const root = document.documentElement
  root.style.setProperty('--safe-area-inset-top', `${top}px`)
  root.style.setProperty('--safe-area-inset-right', `${measured.right}px`)
  root.style.setProperty('--safe-area-inset-bottom', `${measured.bottom}px`)
  root.style.setProperty('--safe-area-inset-left', `${measured.left}px`)
  root.dataset.safeAreaReady = 'true'

  if (isPwaStandalone()) {
    root.classList.add('is-standalone')
  } else {
    root.classList.remove('is-standalone')
  }
}

/** 供 Ant Design Message 等全局浮层读取顶部偏移 */
export function getSafeAreaTopPx () {
  if (typeof document === 'undefined') return 0
  const raw = getComputedStyle(document.documentElement).getPropertyValue('--safe-area-inset-top')
  return parseFloat(raw) || 0
}

export function initSafeArea () {
  if (isInitialized || typeof window === 'undefined') return
  isInitialized = true

  updateSafeAreaVars()

  const debouncedUpdate = debounce(updateSafeAreaVars, 150)
  window.addEventListener('resize', debouncedUpdate, { passive: true })
  window.addEventListener('orientationchange', updateSafeAreaVars)

  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', debouncedUpdate)
  }

  window.matchMedia('(display-mode: standalone)').addEventListener?.('change', updateSafeAreaVars)
  window.matchMedia('(display-mode: fullscreen)').addEventListener?.('change', updateSafeAreaVars)
}

export default {
  initSafeArea,
  updateSafeAreaVars,
  getSafeAreaTopPx
}
